import { css } from "@emotion/react"
import React, { type ElementType } from "react"
import type { FontSizeToken } from "../tokens"
import { cssVariables, tokenVar } from "../theme"
import type { PolymorphicProps } from "./polymorphic"

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

interface HeadingOwnProps {
  readonly level?: HeadingLevel
  readonly size?: "sm" | "md" | "lg" | "xl"
}

export type HeadingProps<Component extends ElementType = "h2"> =
  PolymorphicProps<Component, HeadingOwnProps>

const headingSizes = {
  sm: css`
    font-size: ${tokenVar(cssVariables.fontSize.xl)};
  `,
  md: css`
    font-size: ${tokenVar(cssVariables.fontSize["2xl"])};
  `,
  lg: css`
    font-size: ${tokenVar(cssVariables.fontSize["3xl"])};
  `,
  xl: css`
    font-size: ${tokenVar(cssVariables.fontSize["4xl"])};
  `,
} as const

export const Heading = <Component extends ElementType = "h2">({
  as,
  level = 2,
  size = "md",
  css: cssProp,
  ...props
}: HeadingProps<Component>) => {
  const Element = as ?? (`h${String(level)}` as ElementType)

  return (
    <Element
      css={[
        css`
          margin: 0;
          color: inherit;
          font-family: ${tokenVar(cssVariables.font.body)};
          font-weight: ${tokenVar(cssVariables.fontWeight.black)};
          line-height: ${tokenVar(cssVariables.lineHeight.heading)};
          letter-spacing: -0.035em;
        `,
        headingSizes[size],
        cssProp,
      ]}
      {...props}
    />
  )
}

interface TextOwnProps {
  readonly size?: FontSizeToken
  readonly tone?:
    "default" | "muted" | "subtle" | "accent" | "success" | "danger"
}

export type TextProps<Component extends ElementType = "p"> = PolymorphicProps<
  Component,
  TextOwnProps
>

const toneVariables = {
  default: cssVariables.semanticColor.text,
  muted: cssVariables.semanticColor.textMuted,
  subtle: cssVariables.semanticColor.textSubtle,
  accent: cssVariables.semanticColor.accent,
  success: cssVariables.semanticColor.success,
  danger: cssVariables.semanticColor.danger,
} as const

export const Text = <Component extends ElementType = "p">({
  as,
  size = "md",
  tone = "default",
  css: cssProp,
  ...props
}: TextProps<Component>) => {
  const Element = as ?? "p"

  return (
    <Element
      css={[
        css`
          margin: 0;
          color: ${tokenVar(toneVariables[tone])};
          font-family: ${tokenVar(cssVariables.font.body)};
          font-size: ${tokenVar(cssVariables.fontSize[size])};
          line-height: ${tokenVar(cssVariables.lineHeight.body)};
        `,
        cssProp,
      ]}
      {...props}
    />
  )
}
